import { KeyboardArrowRight } from '@mui/icons-material';
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { t } from 'i18next';
import { Fragment, useEffect, useState } from 'react';
import { DataChip } from '../../components/DataChip';
import { SEVERITY_COLORS, getTagLabel } from '../../enums/Severity';
import { IssueDetailsController } from '../controllers/IssueDetailsController';
import { Issue } from '../models/Issue';

type Props = {
  data: Issue[];
  defaultOpenIssue?: Issue | null;
};

/**
 * Table to display Issues data.
 *
 * Handles issue open state.
 */
export const IssuesTable = ({ data, defaultOpenIssue = null }: Props) => {
  const [initialOpenIssue] = useState(defaultOpenIssue);
  const [openIssues, setOpenIssues] = useState<Map<number, boolean>>(
    new Map<number, boolean>(defaultOpenIssue !== null ? [[defaultOpenIssue.getId(), true]] : [])
  );

  const toggleOpenIssue = (model: Issue) => {
    // Toggle current filter value
    const id = model.getId();
    const currentValue = openIssues.get(id);
    const newMap = new Map<number, boolean>(openIssues);
    newMap.set(id, !currentValue);

    setOpenIssues(newMap);
  };

  // Scroll to element if present on screen after the initial render
  useEffect(() => {
    if (initialOpenIssue === null) {
      return;
    }

    const timeout = setTimeout(() => {
      const element = document.querySelector(`[data-id="issue-${initialOpenIssue.getId()}"]`);
      element?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(timeout);
  }, [initialOpenIssue]);

  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', minWidth: 560 }} role="presentation">
        <TableHead>
          <TableRow>
            <TableCell>{t('issue')}</TableCell>
            <TableCell width={150} align="right">
              {t('reportsImpacted')}
            </TableCell>
            <TableCell align="right" width={125}>
              {t('severity.severity')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((model) => {
            const isOpen = openIssues.get(model.getId());
            const isDisabled = model.getInSlotsPresent() === 0;

            return (
              <Fragment key={model.getId()}>
                <TableRow
                  sx={{
                    transition: 'background-color 0.3s ease-in-out',
                    ...(isOpen
                      ? {
                          backgroundColor: 'backdrops.light',
                          '& td': { border: 'none' },
                        }
                      : {}),
                    ...(isDisabled
                      ? {}
                      : {
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'backdrops.light' },
                        }),
                  }}
                  onClick={isDisabled ? undefined : () => toggleOpenIssue(model)}
                  data-id={`issue-${model.getId()}`}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <IconButton
                        title={t(`a11y.organization.projects.${isOpen ? 'collapseIssueRow' : 'expandIssueRow'}`, {
                          issueNumber: model.getNumber(),
                          issueName: model.getName(),
                        })}
                        size="small"
                        sx={{ color: 'black', p: 0, mx: 0, mr: 2, minWidth: 0 }}
                        disabled={isDisabled}
                      >
                        <KeyboardArrowRight
                          sx={{
                            transition: 'transform 0.1s ease-in-out',
                            transform: `rotate(${isOpen ? 90 : 0}deg)`,
                          }}
                        />
                      </IconButton>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {model.getNumber()}. {model.getName()}
                        </Typography>
                        <Typography variant="body2">{model.getDescription()}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{model.getInSlotsPresent().format()}</TableCell>
                  <TableCell align="right">
                    <DataChip
                      textColor={SEVERITY_COLORS[model.getLevel()].text}
                      borderColor={SEVERITY_COLORS[model.getLevel()].main}
                      backgroundColor={SEVERITY_COLORS[model.getLevel()].background}
                      label={getTagLabel(model.getLevel())}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ p: 0, ...(!isOpen ? { border: 'none' } : {}) }} colSpan={3}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit sx={{ backgroundColor: 'backdrops.light' }}>
                      <IssueDetailsController issue={model} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
